package com.corbettcode.allaboutrocks.pages

import androidx.compose.runtime.Composable
import com.corbettcode.allaboutrocks.HERO_IMAGE_MAX_WIDTH
import com.corbettcode.allaboutrocks.HeadlineTextStyle
import com.corbettcode.allaboutrocks.HeroContainerStyle
import com.corbettcode.allaboutrocks.PageHeadlineStyle
import com.corbettcode.allaboutrocks.PageListStyle
import com.corbettcode.allaboutrocks.PageSectionTitleStyle
import com.corbettcode.allaboutrocks.components.form.ContactForm
import com.corbettcode.allaboutrocks.components.layouts.PageLayoutData
import com.corbettcode.allaboutrocks.utility.Constant.AWESOME_PLACES_EMAIL_ADDRESS
import com.corbettcode.allaboutrocks.utility.textColor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.dom.B
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

@InitRoute
fun initAwesomePlacesPage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Awesome Places"))
}

/**
 * One collecting site.
 *
 * [access] says what kind of land it is rather than naming a price or a permit number, because fees,
 * seasons, and permit rules change from year to year. The intro tells readers to check before going.
 */
private data class Place(
    val name: String
    , val state: String
    , val finds: String
    , val access: String
)

/** The twenty sites, grouped the way you would plan a trip: by where they are. */
private val placesByRegion = listOf(
    "Pacific Northwest" to listOf(
        Place("Glass Buttes", "Oregon", "Rainbow, mahogany, snowflake, and midnight-lace obsidian scattered over the surface for miles.", "BLM public land")
        , Place("Oregon Sunstone Public Collecting Area", "Oregon", "Oregon's state gem — clear to peach crystals, the best of them flashing copper schiller.", "BLM public land")
        , Place("Walker Valley", "Washington", "Geodes and blue agate weathering out of the hillsides north of Mount Vernon.", "State DNR land")
        , Place("Red Top Mountain", "Washington", "Blue agate and thundereggs in the ridges above Blewett Pass.", "National forest")
    )
    , "California & Nevada" to listOf(
        Place("Hauser Geode Beds", "California", "Hollow geodes lined with quartz, dug out of the ash beds near Wiley Well.", "BLM public land")
        , Place("Virgin Valley", "Nevada", "Black opal with fire, some of it replacing buried wood.", "Fee dig")
    )
    , "Southwest" to listOf(
        Place("Topaz Mountain", "Utah", "Sherry-colored topaz crystals in rhyolite cavities, clearing to water-white in sunlight.", "BLM public area")
        , Place("Dugway Geode Beds", "Utah", "Geodes with quartz and amethyst interiors, left behind in an ancient lakebed.", "BLM public land")
        , Place("Rockhound State Park", "New Mexico", "Thundereggs, jasper, perlite, and common opal — one of the few parks that lets you keep what you find.", "State park")
    )
    , "Rocky Mountains" to listOf(
        Place("Crystal Park", "Montana", "Quartz crystals, including scepters and amethyst, screened out of decomposed granite.", "National forest, day use")
        , Place("Gem Mountain", "Montana", "Montana sapphires in every shade, washed from gravel concentrate.", "Fee dig")
        , Place("Blue Forest", "Wyoming", "Petrified wood wrapped in blue-white algal limestone, from a drowned Eocene forest.", "BLM public land")
    )
    , "Midwest & Great Lakes" to listOf(
        Place("Lake Superior agate beaches", "Minnesota", "Red and orange banded agates, tumbled by glaciers and then by the lake.", "Public beaches")
        , Place("Petoskey stone beaches", "Michigan", "Petoskey stones — fossil colony coral that shows its honeycomb pattern when wet.", "Public beaches and state parks")
        , Place("Keweenaw copper country", "Michigan", "Native copper, datolite, and agate on the old mine rock piles.", "Fee dig")
    )
    , "South & Southeast" to listOf(
        Place("Crater of Diamonds State Park", "Arkansas", "Real diamonds in an eroded volcanic pipe — the only public diamond site of its kind, and you keep what you find.", "State park")
        , Place("Mount Ida", "Arkansas", "Clear quartz crystals, singles and plates, from the Ouachitas.", "Fee dig and national forest")
        , Place("Emerald Hollow Mine", "North Carolina", "Emerald, along with garnet, sapphire, and smoky quartz in the same creek gravel.", "Fee dig")
    )
    , "Northeast" to listOf(
        Place("Herkimer Diamond Mines", "New York", "\"Herkimer diamonds\" — doubly terminated quartz crystals, water-clear and already faceted by nature.", "Fee dig")
        , Place("Oxford County", "Maine", "Tourmaline, beryl, and rose quartz from the pegmatite belt around Paris and Newry.", "Fee dig")
    )
)

@Composable
private fun PlacesSectionTitle(text: String) {
    SpanText(
        text
        , PageSectionTitleStyle.toModifier()
            .color(textColor())
    )
}

@Composable
private fun PlacesList(places: List<Place>) {
    Ul(PageListStyle.toAttrs()) {
        places.forEach { place ->
            Li {
                B { Text("${place.name}, ${place.state}") }
                Text(" — ${place.finds} ")
                Em { Text("(${place.access})") }
            }
        }
    }
}

@Composable
private fun AwesomePlacesHeadline() {
    Div(HeadlineTextStyle.toAttrs()) {
        Column {
            SpanText(
                "Awesome Places to Explore"
                , PageHeadlineStyle.toModifier()
                    .color(textColor())
                    .align(alignment = CenterHorizontally)
            )
        }
    }
}

@Composable
private fun AwesomePlacesHero() {

    AwesomePlacesHeadline()

    // 1 column on mobile, 2 columns on md+ (image left, text right)
    SimpleGrid(
        numColumns = numColumns(1, md = 2)
        , modifier = HeroContainerStyle
            .toModifier()
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        Box {
            Column(Modifier.gap(2.cssRem)) {
                Image(
                    src = "/images/awesome_places/rock_hammer.webp"
                    , alt = "A rock hammer, the one tool no rockhound leaves behind"
                    , modifier = Modifier
                        .maxWidth(HERO_IMAGE_MAX_WIDTH)
                        .fillMaxWidth()  // shrinks naturally on small screens
                )
                P { Em { Text("Twenty places where the collecting is good and the public is welcome.") } }
            }
        }

        Column(Modifier.gap(2.cssRem)) {
            P {
                Text("Half of rockhounding is knowing where to point the truck. These twenty sites are the ones that keep coming up — public land where you can fill a bucket for nothing, and a handful of long-running fee digs famous enough to be worth the admission. They are grouped by region, so you can find what is within a day's drive and what is worth saving for a proper trip.")
            }

            P {
                Text("One rule before you go: check the current rules with whoever manages the land. Fees change, seasons close, permits get added, and a site that was open last summer may not be this one. A phone call to the ranger district, the state park, or the mine office costs nothing and saves a wasted weekend.")
            }
        }
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        placesByRegion.forEach { (region, places) ->
            PlacesSectionTitle(region)
            PlacesList(places)
        }
    }

    HorizontalDivider(Modifier
        .fillMaxWidth()
        .margin(topBottom = 2.cssRem)
    )

    Column(
        Modifier
            .fillMaxWidth()
            .gap(2.cssRem)
    ) {
        PlacesSectionTitle("Suggest a Place")
        P {
            Text("Know somewhere that belongs on this list? Send it over and we will take a look.")
        }
        ContactForm(AWESOME_PLACES_EMAIL_ADDRESS)
    }
}

@Page("awesome-places")
@Layout(".components.layouts.PageLayout")
@Composable
fun AwesomePlacesPage() {
    AwesomePlacesHero()
}
